import {
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Stack from '@mui/material/Stack';
import { useNavigate, useParams } from 'react-router-dom';

import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import { showSuiErrorSnackbar } from 'components/SuiSnackbar';
import SoftTypography from 'components/SoftTypography';

import { useAuth } from 'auth';
import {
  fetchAccountConversations,
  fetchConversation,
  fetchConversationMessages,
  markConversationMessagesReadRequest,
  readCachedAccountConversations,
  readCachedConversation,
  readCachedConversationMessages,
  sendConversationMessageRequest,
  type ChatAccount,
  type ChatConversation,
  type ConversationMessage,
} from 'chat';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { useLocale } from 'lib/i18n/client';
import { getTranslations, interpolateTemplate } from 'lib/i18n/dictionary';

function Messages() {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid?: string }>();
  const { account, accessToken } = useAuth();
  const locale = useLocale();
  const translations = getTranslations(locale);
  const streamRef = useRef<HTMLDivElement | null>(null);
  const streamEndRef = useRef<HTMLDivElement | null>(null);
  const selectedConversationUuid = uuid?.trim() || null;
  const cachedConversations = account?.uuid
    ? readCachedAccountConversations(account.uuid)
    : undefined;
  const cachedSelectedConversation = selectedConversationUuid
    ? readCachedConversation(selectedConversationUuid)
    : undefined;
  const cachedMessages = selectedConversationUuid
    ? readCachedConversationMessages(selectedConversationUuid)
    : undefined;
  const [draft, setDraft] = useState('');
  const [conversations, setConversations] = useState<ChatConversation[]>(
    () => cachedConversations ?? []
  );
  const [selectedConversation, setSelectedConversation] = useState<ChatConversation | null>(
    () => cachedSelectedConversation ?? null
  );
  const [messages, setMessages] = useState<ConversationMessage[]>(() => cachedMessages ?? []);
  const [isConversationsLoading, setIsConversationsLoading] = useState(
    () => cachedConversations === undefined
  );
  const [isConversationLoading, setIsConversationLoading] = useState(
    () => selectedConversationUuid !== null && cachedSelectedConversation === undefined
  );
  const [isMessagesLoading, setIsMessagesLoading] = useState(
    () => selectedConversationUuid !== null && cachedMessages === undefined
  );
  const [isSending, setIsSending] = useState(false);

  function scrollToLatestMessage(behavior: ScrollBehavior) {
    streamEndRef.current?.scrollIntoView({ block: 'end', behavior });

    const streamElement = streamRef.current;
    if (!streamElement) {
      return;
    }

    streamElement.scrollTo({
      top: streamElement.scrollHeight,
      behavior,
    });
  }

  const loadConversations = useCallback(async () => {
    if (!account?.uuid) {
      setConversations([]);
      setIsConversationsLoading(false);
      return;
    }

    const cachedData = readCachedAccountConversations(account.uuid);
    if (cachedData !== undefined) {
      setConversations(cachedData);
      setIsConversationsLoading(false);
    } else {
      setIsConversationsLoading(true);
    }

    try {
      const nextConversations = await fetchAccountConversations(
        account.uuid,
        accessToken ?? undefined
      );
      setConversations(nextConversations);
    } catch (error) {
      showSuiErrorSnackbar(
        error instanceof Error ? error.message : translations.messagesPage.loadFailed
      );
    } finally {
      setIsConversationsLoading(false);
    }
  }, [accessToken, account?.uuid, translations.messagesPage.loadFailed]);

  const loadSelectedConversation = useCallback(async () => {
    if (!selectedConversationUuid) {
      setSelectedConversation(null);
      setMessages([]);
      setDraft('');
      setIsConversationLoading(false);
      setIsMessagesLoading(false);
      return;
    }

    const cachedConversation = readCachedConversation(selectedConversationUuid);
    const cachedConversationMessages = readCachedConversationMessages(selectedConversationUuid);

    if (cachedConversation !== undefined) {
      setSelectedConversation(cachedConversation);
      setIsConversationLoading(false);
    } else {
      setSelectedConversation(null);
      setIsConversationLoading(true);
    }

    if (cachedConversationMessages !== undefined) {
      setMessages(cachedConversationMessages);
      setIsMessagesLoading(false);
    } else {
      setMessages([]);
      setIsMessagesLoading(true);
    }

    try {
      const [fetchedConversation, fetchedMessages] = await Promise.all([
        fetchConversation(selectedConversationUuid, accessToken ?? undefined),
        fetchConversationMessages(selectedConversationUuid, accessToken ?? undefined),
      ]);

      const unreadIncomingMessages = account?.uuid
        ? fetchedMessages.filter(
            (message) => message.senderAccountUuid !== account.uuid && !message.isRead
          )
        : [];
      const readMessages =
        unreadIncomingMessages.length > 0
          ? await markConversationMessagesReadRequest(
              selectedConversationUuid,
              accessToken ?? undefined
            )
          : [];
      const nextMessages = mergeConversationMessagesReadState(fetchedMessages, readMessages);
      const nextConversation = fetchedConversation
        ? applyConversationReadState(fetchedConversation, readMessages)
        : fetchedConversation;

      setSelectedConversation(nextConversation);
      setMessages(nextMessages);
      setConversations((currentConversations) =>
        currentConversations.map((conversation) =>
          conversation.uuid === selectedConversationUuid
            ? applyConversationReadState(conversation, readMessages)
            : conversation
        )
      );
    } catch (error) {
      showSuiErrorSnackbar(
        error instanceof Error ? error.message : translations.messagesPage.loadFailed
      );
      setSelectedConversation(null);
      setMessages([]);
    } finally {
      setIsConversationLoading(false);
      setIsMessagesLoading(false);
    }
  }, [accessToken, account?.uuid, selectedConversationUuid, translations.messagesPage.loadFailed]);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    void loadSelectedConversation();
  }, [loadSelectedConversation]);

  useEffect(() => {
    if (!selectedConversationUuid || isConversationLoading || isMessagesLoading) {
      return;
    }

    scrollToLatestMessage('auto');

    const frameId = window.requestAnimationFrame(() => {
      scrollToLatestMessage(isSending ? 'smooth' : 'auto');
    });
    const timeoutId = window.setTimeout(() => {
      scrollToLatestMessage('auto');
    }, 50);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [isConversationLoading, isMessagesLoading, isSending, messages, selectedConversationUuid]);

  const selectedConversationFromList = useMemo(
    () =>
      selectedConversationUuid
        ? conversations.find((conversation) => conversation.uuid === selectedConversationUuid) ??
          null
        : null,
    [conversations, selectedConversationUuid]
  );

  const activeConversation = selectedConversation ?? selectedConversationFromList;
  const activeCounterpart = activeConversation
    ? getCounterpartAccount(activeConversation, account?.uuid ?? '')
    : null;
  const activeConversationTitle = activeConversation
    ? resolveConversationTitle(
        activeConversation,
        activeCounterpart,
        account?.uuid ?? '',
        translations.messagesPage.notFound
      )
    : '';
  const activeConversationSubtitle =
    activeConversation && activeCounterpart
      ? resolveConversationSubtitle(
          activeConversation,
          activeCounterpart,
          translations.messagesPage
        )
      : '';

  const canSend = Boolean(selectedConversationUuid && draft.trim().length > 0 && !isSending);

  async function handleSendMessage() {
    if (!selectedConversationUuid || !draft.trim() || isSending) {
      return;
    }

    const nextDraft = draft.trim();
    setIsSending(true);

    try {
      const createdMessage = await sendConversationMessageRequest(
        selectedConversationUuid,
        nextDraft,
        accessToken ?? undefined
      );
      setMessages((currentMessages) => [...currentMessages, createdMessage]);
      setDraft('');
      await loadConversations();
      const latestConversation = await fetchConversation(
        selectedConversationUuid,
        accessToken ?? undefined
      );
      setSelectedConversation(latestConversation);
    } catch (error) {
      showSuiErrorSnackbar(
        error instanceof Error ? error.message : translations.messagesPage.sendFailed
      );
    } finally {
      setIsSending(false);
    }
  }

  return (
    <DashboardLayout sx={{ pb: 0 }}>
      <DashboardNavbar />
      <SoftBox
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          height: {
            xs: 'auto',
            lg: 'calc(100dvh - 8.5rem)',
          },
          maxHeight: {
            xs: 'none',
            lg: 'calc(100dvh - 8.5rem)',
          },
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Card
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            height: '100%',
          }}
        >
          <SoftBox
            p={3}
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexDirection={{ xs: 'column', md: 'row' }}
            gap={2}
            sx={({ palette }) => ({
              flexShrink: 0,
              borderBottom: `1px solid ${palette.grey[200]}`,
            })}
          >
            <SoftBox lineHeight={1}>
              <SoftTypography variant="h5" fontWeight="medium">
                {translations.messagesPage.heading}
              </SoftTypography>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                {translations.messagesPage.subtitle}
              </SoftTypography>
            </SoftBox>
            {activeConversation ? (
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems="flex-start"
                sx={{ maxWidth: { xs: '100%', md: '60%' } }}
              >
                <SoftBox textAlign={{ xs: 'left', md: 'right' }} minWidth={0}>
                  <SoftTypography variant="button" fontWeight="bold" sx={{ display: 'block' }}>
                    {activeConversationTitle}
                  </SoftTypography>
                  <SoftTypography
                    variant="caption"
                    color="text"
                    sx={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {activeConversationSubtitle}
                  </SoftTypography>
                </SoftBox>
                <Chip
                  label={resolveConversationTypeLabel(
                    activeConversation.type,
                    translations.messagesPage
                  )}
                  color="info"
                  variant="outlined"
                  size="small"
                />
              </Stack>
            ) : null}
          </SoftBox>

          <SoftBox
            sx={{
              display: 'grid',
              flex: 1,
              minHeight: 0,
              height: '100%',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'minmax(18rem, 0.8fr) minmax(0, 2.2fr)',
              },
              gridTemplateRows: {
                xs: 'auto',
                lg: 'minmax(0, 1fr)',
              },
              overflow: 'hidden',
            }}
          >
          <SoftBox sx={{ display: 'flex', minHeight: 0, height: '100%', overflow: 'hidden' }}>
            <SoftBox
              sx={({ palette }) => ({
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
                width: '100%',
                borderRight: { xs: 'none', lg: `1px solid ${palette.grey[200]}` },
              })}
            >
              <SoftBox
                sx={{
                  flex: 1,
                  height: 0,
                  minHeight: 0,
                  overflowY: 'auto',
                }}
              >
                {isConversationsLoading ? (
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minHeight={320}
                    p={3}
                    gap={1.5}
                  >
                    <CircularProgress color="info" size={28} />
                    <SoftTypography variant="button" color="text">
                      {translations.messagesPage.loadingConversations}
                    </SoftTypography>
                  </SoftBox>
                ) : !account?.uuid ? (
                  <EmptyPanelMessage message={translations.messagesPage.accountMissing} />
                ) : conversations.length === 0 ? (
                  <EmptyPanelMessage message={translations.messagesPage.emptyTitle} />
                ) : (
                  conversations.map((conversation, index) => {
                    const counterpart = getCounterpartAccount(
                      conversation,
                      account?.uuid ?? ''
                    );
                    const title = resolveConversationTitle(
                      conversation,
                      counterpart,
                      account?.uuid ?? '',
                      translations.messagesPage.notFound
                    );
                    const subtitle = resolveConversationSubtitle(
                      conversation,
                      counterpart,
                      translations.messagesPage
                    );
                    const preview = formatConversationPreview(
                      conversation,
                      counterpart,
                      account?.uuid ?? '',
                      translations.messagesPage,
                      translations.chatPage.you
                    );
                    const isSelected = conversation.uuid === selectedConversationUuid;

                    return (
                      <SoftBox key={conversation.uuid}>
                        <SoftBox
                          component="button"
                          type="button"
                          onClick={() => navigate(`/messages/${conversation.uuid}`)}
                          sx={({ palette, functions: { pxToRem } }) => ({
                            width: '100%',
                            border: 0,
                            background: isSelected
                              ? 'rgba(var(--matcha-main-rgb), 0.12)'
                              : 'transparent',
                            textAlign: 'left',
                            padding: pxToRem(18),
                            cursor: 'pointer',
                            borderRadius: 0,
                            borderLeft: isSelected
                              ? `${pxToRem(4)} solid ${palette.info.main}`
                              : `${pxToRem(4)} solid transparent`,
                            transition: 'background-color 160ms ease',
                            '&:hover': {
                              background: isSelected
                                ? 'rgba(var(--matcha-main-rgb), 0.16)'
                                : palette.grey[100],
                            },
                            '&:focus-visible': {
                              outline: `2px solid ${palette.info.main}`,
                              outlineOffset: -2,
                            },
                          })}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <AvatarPill label={getConversationInitials(conversation, counterpart)} />
                            <SoftBox minWidth={0} flex={1}>
                              <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                gap={1}
                              >
                                <SoftTypography
                                  variant="button"
                                  fontWeight={conversation.hasUnreadMessages ? 700 : 500}
                                  sx={{ display: 'block' }}
                                >
                                  {title}
                                </SoftTypography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <SoftTypography
                                    variant="caption"
                                    color={conversation.hasUnreadMessages ? 'dark' : 'text'}
                                    fontWeight={conversation.hasUnreadMessages ? 700 : 400}
                                  >
                                    {formatConversationDate(
                                      conversation.lastMessage?.createdAt ?? conversation.updatedAt
                                    )}
                                  </SoftTypography>
                                  {conversation.hasUnreadMessages ? (
                                    <SoftBox
                                      sx={({ palette }) => ({
                                        width: 8,
                                        height: 8,
                                        borderRadius: 999,
                                        backgroundColor: palette.info.main,
                                        flexShrink: 0,
                                      })}
                                    />
                                  ) : null}
                                </Stack>
                              </SoftBox>
                              <SoftTypography
                                variant="caption"
                                color="text"
                                sx={{ display: 'block', mt: 0.25 }}
                              >
                                {subtitle}
                              </SoftTypography>
                              <SoftTypography
                                variant="caption"
                                color={conversation.hasUnreadMessages ? 'dark' : 'text'}
                                fontWeight={conversation.hasUnreadMessages ? 700 : 400}
                                sx={{
                                  display: '-webkit-box',
                                  overflow: 'hidden',
                                  mt: 1,
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                }}
                              >
                                {preview}
                              </SoftTypography>
                            </SoftBox>
                          </Stack>
                        </SoftBox>
                        {index < conversations.length - 1 ? <Divider /> : null}
                      </SoftBox>
                    );
                  })
                )}
              </SoftBox>
            </SoftBox>
          </SoftBox>

          <SoftBox sx={{ display: 'flex', minHeight: 0, height: '100%', overflow: 'hidden' }}>
            <SoftBox
              sx={{
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                width: '100%',
              }}
            >
              {activeConversation ? (
                <>
                  <SoftBox
                    ref={streamRef}
                    sx={({ palette }) => ({
                      flex: 1,
                      height: 0,
                      minHeight: 0,
                      overflowY: 'auto',
                      background: `linear-gradient(180deg, ${palette.grey[100]} 0%, ${palette.background.default} 100%)`,
                    })}
                  >
                    {isConversationLoading || isMessagesLoading ? (
                      <SoftBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        minHeight={320}
                        gap={1.5}
                      >
                        <CircularProgress color="info" size={28} />
                        <SoftTypography variant="button" color="text">
                          {translations.messagesPage.loadingMessages}
                        </SoftTypography>
                      </SoftBox>
                    ) : !selectedConversation && selectedConversationUuid ? (
                      <EmptyPanelMessage message={translations.messagesPage.notFound} />
                    ) : messages.length === 0 ? (
                      <EmptyPanelMessage message={translations.messagesPage.noMessages} />
                    ) : (
                      <SoftBox p={3} display="flex" flexDirection="column" gap={2}>
                        {messages.map((message, index) => {
                          const isOwnMessage = message.senderAccountUuid === account?.uuid;
                          const shouldShowDate =
                            index === 0 ||
                            formatConversationDate(message.createdAt) !==
                              formatConversationDate(messages[index - 1].createdAt);

                          return (
                            <SoftBox key={message.uuid}>
                              {shouldShowDate ? (
                                <SoftBox textAlign="center" mb={2}>
                                  <SoftTypography variant="caption" color="text" fontWeight="bold">
                                    {interpolateTemplate(
                                      translations.messagesPage.createdAtLabel,
                                      {
                                        date: formatConversationDate(message.createdAt),
                                      }
                                    )}
                                  </SoftTypography>
                                </SoftBox>
                              ) : null}
                              <SoftBox
                                display="flex"
                                justifyContent={isOwnMessage ? 'flex-end' : 'flex-start'}
                              >
                                <SoftBox
                                  sx={({ palette, borders, boxShadows }) => ({
                                    maxWidth: '78%',
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: borders.borderRadius.xl,
                                    borderBottomRightRadius: isOwnMessage
                                      ? borders.borderRadius.sm
                                      : borders.borderRadius.xl,
                                    borderBottomLeftRadius: isOwnMessage
                                      ? borders.borderRadius.xl
                                      : borders.borderRadius.sm,
                                    background: isOwnMessage
                                      ? palette.info.main
                                      : palette.white.main,
                                    border: isOwnMessage
                                      ? 'none'
                                      : `1px solid ${palette.grey[300]}`,
                                    boxShadow: boxShadows.sm,
                                  })}
                                >
                                  <SoftTypography
                                    variant="caption"
                                    color={isOwnMessage ? 'white' : 'dark'}
                                    sx={{ whiteSpace: 'pre-wrap' }}
                                  >
                                    {message.body}
                                  </SoftTypography>
                                </SoftBox>
                              </SoftBox>
                              <SoftBox
                                mt={0.75}
                                display="flex"
                                justifyContent={isOwnMessage ? 'flex-end' : 'flex-start'}
                              >
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <SoftTypography variant="caption" color="text">
                                    {formatMessageTime(message.createdAt)}
                                  </SoftTypography>
                                  {isOwnMessage ? (
                                    <Icon
                                      sx={({ palette }) => ({
                                        fontSize: 14,
                                        color: message.isRead
                                          ? palette.info.main
                                          : palette.grey[500],
                                      })}
                                    >
                                      {message.isRead ? 'done_all' : 'done'}
                                    </Icon>
                                  ) : null}
                                </Stack>
                              </SoftBox>
                            </SoftBox>
                          );
                        })}
                        <div ref={streamEndRef} aria-hidden="true" />
                      </SoftBox>
                    )}
                  </SoftBox>
                  <SoftBox p={2.5}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <SoftBox flex={1}>
                        <SoftInput
                          placeholder={translations.messagesPage.messagePlaceholder}
                          value={draft}
                          onChange={(event: { target: { value: string } }) =>
                            setDraft(event.target.value)
                          }
                          onKeyDown={(event: ReactKeyboardEvent<HTMLInputElement>) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                              event.preventDefault();
                              void handleSendMessage();
                            }
                          }}
                          multiline
                        />
                      </SoftBox>
                      <SoftButton disabled={!canSend} onClick={() => void handleSendMessage()}>
                        {isSending
                          ? translations.messagesPage.sending
                          : translations.messagesPage.sendAction}
                      </SoftButton>
                    </Stack>
                  </SoftBox>
                </>
              ) : (
                <SoftBox
                  height="100%"
                  minHeight={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  p={4}
                >
                  <SoftBox textAlign="center" maxWidth={420}>
                    <Icon
                      sx={({ palette }) => ({
                        color: palette.info.main,
                        fontSize: 42,
                        mb: 1.5,
                      })}
                    >
                      forum
                    </Icon>
                    <SoftTypography variant="h5" fontWeight="bold">
                      {translations.messagesPage.heading}
                    </SoftTypography>
                    <SoftTypography variant="button" color="text">
                      {translations.messagesPage.emptyConversation}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
              )}
            </SoftBox>
          </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

function EmptyPanelMessage({ message }: { message: string }) {
  return (
    <SoftBox
      minHeight={320}
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
      textAlign="center"
    >
      <SoftTypography variant="button" color="text">
        {message}
      </SoftTypography>
    </SoftBox>
  );
}

function AvatarPill({ label }: { label: string }) {
  return (
    <SoftBox
      sx={({ palette }) => ({
        width: 44,
        height: 44,
        borderRadius: 999,
        background: `linear-gradient(135deg, ${palette.info.main}, ${palette.success.main})`,
        color: palette.white.main,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.85rem',
        fontWeight: 700,
        flexShrink: 0,
      })}
    >
      {label}
    </SoftBox>
  );
}

function mergeConversationMessagesReadState(
  messages: ConversationMessage[],
  readMessages: ConversationMessage[]
) {
  if (!readMessages.length) {
    return messages;
  }

  const readMessageByUuid = new Map(readMessages.map((message) => [message.uuid, message]));
  return messages.map((message) => readMessageByUuid.get(message.uuid) ?? message);
}

function applyConversationReadState(
  conversation: ChatConversation,
  readMessages: ConversationMessage[]
) {
  if (!readMessages.length) {
    return conversation;
  }

  const readMessageUuids = new Set(readMessages.map((message) => message.uuid));

  return {
    ...conversation,
    hasUnreadMessages: false,
    lastMessage:
      conversation.lastMessage && readMessageUuids.has(conversation.lastMessage.uuid)
        ? {
            ...conversation.lastMessage,
            isRead: true,
          }
        : conversation.lastMessage,
  };
}

function getCounterpartAccount(conversation: ChatConversation, accountUuid: string): ChatAccount {
  return conversation.ownerAccountUuid === accountUuid
    ? conversation.participantAccount
    : conversation.ownerAccount;
}

function formatAccountFullName(account: ChatAccount): string | null {
  const primaryProfile = account.profiles[0];
  const fullName = [primaryProfile?.firstName, primaryProfile?.lastName]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(' ');

  if (fullName) {
    return fullName;
  }

  if (primaryProfile?.title?.trim()) {
    return primaryProfile.title.trim();
  }

  return null;
}

function formatAccountDisplayName(
  account: ChatAccount,
  fallback: string
): string {
  return formatAccountFullName(account) ?? fallback;
}

function resolveConversationTitle(
  conversation: ChatConversation,
  counterpart: ChatAccount | null,
  accountUuid: string,
  fallback: string
): string {
  if (conversation.type === 'POSITION' && conversation.position?.title?.trim()) {
    return conversation.position.title.trim();
  }

  if (conversation.type === 'EVENT' && conversation.event?.title?.trim()) {
    return conversation.event.title.trim();
  }

  if (!counterpart) {
    counterpart = getCounterpartAccount(conversation, accountUuid);
  }

  return counterpart ? formatAccountDisplayName(counterpart, fallback) : fallback;
}

function resolveConversationSubtitle(
  conversation: ChatConversation,
  counterpart: ChatAccount,
  messagesPage: ReturnType<typeof getTranslations>['messagesPage']
) {
  const name = formatAccountDisplayName(counterpart, messagesPage.unknownParticipant);

  if (conversation.type === 'POSITION') {
    return interpolateTemplate(messagesPage.positionConversationSubtitle, {
      name,
    });
  }

  if (conversation.type === 'EVENT') {
    return interpolateTemplate(messagesPage.eventConversationSubtitle, {
      name,
    });
  }

  return interpolateTemplate(messagesPage.directConversationSubtitle, {
    name,
  });
}

function resolveConversationTypeLabel(
  type: ChatConversation['type'],
  messagesPage: ReturnType<typeof getTranslations>['messagesPage']
) {
  if (type === 'POSITION') {
    return messagesPage.typePosition;
  }

  if (type === 'EVENT') {
    return messagesPage.typeEvent;
  }

  return messagesPage.typeDirect;
}

function formatConversationPreview(
  conversation: ChatConversation,
  counterpart: ChatAccount,
  accountUuid: string,
  messagesPage: ReturnType<typeof getTranslations>['messagesPage'],
  youLabel: string
) {
  const body = conversation.lastMessage?.body?.trim();
  if (!body) {
    return messagesPage.noMessages;
  }

  const isOwnMessage = conversation.lastMessage?.senderAccountUuid === accountUuid;
  const senderLabel = isOwnMessage
    ? youLabel
    : formatAccountDisplayName(counterpart, messagesPage.unknownParticipant);

  return `${senderLabel}: ${body}`;
}

function getConversationInitials(conversation: ChatConversation, counterpart: ChatAccount) {
  if (conversation.type === 'POSITION' && conversation.position?.title?.trim()) {
    return conversation.position.title
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((segment) => segment[0]?.toUpperCase() ?? '')
      .join('');
  }

  const displayName = formatAccountDisplayName(counterpart, '');
  return displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((segment) => segment[0]?.toUpperCase() ?? '')
    .join('');
}

function formatConversationDate(value: string) {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(timestamp));
}

function formatMessageTime(value: string) {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

export default Messages;
